import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileImage, X } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".jpg,.jpeg,.png";

export default function ClientReceiptReader({ clientId, clientName }) {
  const fileRef = useRef(null);
  const [pickedFile, setPickedFile] = useState(null);
  const [records, setRecords] = useState([]);

  const updateRecord = (index, field, value) => {
    setRecords((prev) =>
      prev.map((record, i) =>
        i === index ? { ...record, [field]: value } : record
      )
    );
  };

  const exportExcel = async () => {
    if (!records.length) {
      toast.error("Excel oluşturmak için fiş kaydı yok");
      return;
    }

    try {
      const res = await api.post(
        `/clients/${clientId}/receipt-reader/xlsx`,
        { records },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${clientName || "Mukellef"}_Fis_Listesi.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel dosyası oluşturuldu");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Excel oluşturulamadı");
    }
  };

  const onPick = (file) => {
    if (!file) return;

    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      toast.error("Sadece JPG, JPEG veya PNG fotoğraf yükleyebilirsiniz");
      return;
    }

    setPickedFile(file);
  };

  const clearFile = () => {
    setPickedFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Fiş Okuma</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {clientName} için bir veya birden fazla fiş içeren fotoğrafı yükleyin.
        </p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
      />

      {!pickedFile ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full min-h-48 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-3 hover:bg-muted/40 transition-colors"
        >
          <Upload size={32} className="text-muted-foreground" />
          <div className="text-center">
            <div className="font-medium">Fiş fotoğrafı seçin</div>
            <div className="text-sm text-muted-foreground mt-1">
              JPG, JPEG veya PNG
            </div>
          </div>
        </button>
      ) : (
        <div className="border rounded-lg p-4 flex items-center gap-3">
          <FileImage size={28} className="text-muted-foreground" />

          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{pickedFile.name}</div>
            <div className="text-xs text-muted-foreground">
              {(pickedFile.size / 1024 / 1024).toFixed(2)} MB
            </div>
          </div>

          <Button variant="ghost" size="icon" onClick={clearFile}>
            <X size={18} />
          </Button>
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <Button
          disabled={!pickedFile}
          onClick={async () => {
            try {
              const fd = new FormData();
              fd.append("file", pickedFile);
              const res = await api.post(`/clients/${clientId}/receipt-reader/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
              setRecords(res.data.records || []);
              toast.success(`${(res.data.records || []).length} fiş okundu`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Fotoğraf yüklenemedi");
            }
          }}
        >
          Fişleri Oku
        </Button>
      </div>

      {records.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-3 py-2">Tarih</th>
                <th className="px-3 py-2">Fiş No</th>
                <th className="px-3 py-2">VKN/TCKN</th>
                <th className="px-3 py-2">Firma</th>
                <th className="px-3 py-2 text-right">%1 KDV</th>
                <th className="px-3 py-2 text-right">%10 KDV</th>
                <th className="px-3 py-2 text-right">%20 KDV</th>
                <th className="px-3 py-2 text-right">KDV Oranı</th>
                <th className="px-3 py-2 text-right">Matrah</th>
                <th className="px-3 py-2 text-right">Toplam KDV</th>
                <th className="px-3 py-2 text-right">Genel Toplam</th>
                <th className="px-3 py-2 text-right">Güven</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="px-2 py-2">
                    <input
                      value={r.date ?? ""}
                      onChange={(e) => updateRecord(i, "date", e.target.value)}
                      className="w-28 bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={r.receipt_no ?? ""}
                      onChange={(e) => updateRecord(i, "receipt_no", e.target.value)}
                      className="w-24 bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={r.tax_id ?? ""}
                      onChange={(e) => updateRecord(i, "tax_id", e.target.value)}
                      className="w-32 bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={r.company_name ?? ""}
                      onChange={(e) => updateRecord(i, "company_name", e.target.value)}
                      className="min-w-48 w-full bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">{r.vat_1 ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{r.vat_10 ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{r.vat_20 ?? "—"}</td>
                  <td className="px-2 py-2">
                    <input
                      value={r.vat_rate ?? ""}
                      onChange={(e) => updateRecord(i, "vat_rate", e.target.value)}
                      className="w-20 text-right bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={r.tax_base ?? ""}
                      onChange={(e) => updateRecord(i, "tax_base", e.target.value)}
                      className="w-28 text-right bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={r.total_vat ?? ""}
                      onChange={(e) => updateRecord(i, "total_vat", e.target.value)}
                      className="w-28 text-right bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={r.total_amount ?? ""}
                      onChange={(e) => updateRecord(i, "total_amount", e.target.value)}
                      className="w-28 text-right bg-transparent border border-border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {typeof r.confidence === "number" ? `%${Math.round(r.confidence * 100)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-5 flex justify-end">
            <Button onClick={exportExcel}>
              Excel'e Aktar
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
